import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Plus, Minus, TrendingUp, DollarSign, Award, ArrowRight, Info } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { getCommissionTiers, getCurrentTier, getNextTier } from '../utils/commissionTiers';
import { useAuth } from '../context/AuthContext';

const CommissionCalculator = () => {
  const { user } = useAuth();
  const brokerType = user?.broker_type || 'senior';

  const [currentLoans, setCurrentLoans] = useState(2);
  const [loanAmount, setLoanAmount] = useState(380000);
  const [baseRate, setBaseRate] = useState(2.75);
  const [referralLoans, setReferralLoans] = useState(1);
  const [plannedLoans, setPlannedLoans] = useState(3);

  const commissionConfig = getCommissionTiers(brokerType);
  const COMMISSION_TIERS = commissionConfig.tiers;
  const SELF_GEN_RATE = commissionConfig.selfGenRate;

  // Calculate current tier
  const currentTier = getCurrentTier(currentLoans, brokerType);
  const totalPlannedLoans = currentLoans + plannedLoans;
  const projectedTier = getCurrentTier(totalPlannedLoans, brokerType);
  const nextTier = getNextTier(currentLoans, brokerType);

  // Calculate commissions
  const baseCommission = loanAmount * (baseRate / 100);
  
  // Current earnings
  const regularLoans = currentLoans - referralLoans;
  const currentRegularEarnings = regularLoans * baseCommission * currentTier.rate;
  const currentReferralEarnings = referralLoans * baseCommission * SELF_GEN_RATE;
  const currentTotalEarnings = currentRegularEarnings + currentReferralEarnings;

  // Projected earnings
  const projectedRegularLoans = totalPlannedLoans - referralLoans;
  const projectedRegularEarnings = projectedRegularLoans * baseCommission * projectedTier.rate;
  const projectedReferralEarnings = referralLoans * baseCommission * SELF_GEN_RATE;
  const projectedTotalEarnings = projectedRegularEarnings + projectedReferralEarnings;

  const additionalEarnings = projectedTotalEarnings - currentTotalEarnings;

  // Progress to next tier
  const loansToNextTier = nextTier ? nextTier.loans - currentLoans : 0;
  const tierProgress = nextTier ? ((currentLoans / nextTier.loans) * 100) : 100;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (rate) => {
    return `${(rate * 100).toFixed(0)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-2xl lg:text-3xl font-semibold text-white">
          Commission Calculator
        </h1>
        <p className="text-gray-500 text-sm">
          Calculate your commission based on loan count and adjustable parameters
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Inputs */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="bg-[#111] border-[#222]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Calculator Inputs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Closed Loans */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-300">Number of Closed Loans</Label>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentLoans(Math.max(0, currentLoans - 1))}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 text-center">
                    <div className="text-3xl font-bold text-white">{currentLoans}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentLoans(currentLoans + 1)}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Loan Amount */}
              <div className="space-y-2">
                <Label htmlFor="loanAmount" className="text-sm font-medium text-gray-300">
                  Loan Amount ($)
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="loanAmount"
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                    className="pl-10 h-11 bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                  />
                </div>
              </div>

              {/* Base Commission Rate */}
              <div className="space-y-2">
                <Label htmlFor="baseRate" className="text-sm font-medium text-gray-300">
                  Base Commission Rate (%)
                </Label>
                <Input
                  id="baseRate"
                  type="number"
                  step="0.01"
                  value={baseRate}
                  onChange={(e) => setBaseRate(Number(e.target.value))}
                  className="h-11 bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                />
              </div>

              {/* Referral Loans */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-300">Self-Generated Loans</Label>
                  <Badge className="bg-green-900/50 text-green-400 border border-green-800">
                    {formatPercent(SELF_GEN_RATE)} Rate
                  </Badge>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setReferralLoans(Math.max(0, referralLoans - 1))}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 text-center">
                    <div className="text-2xl font-bold text-green-400">{referralLoans}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setReferralLoans(Math.min(currentLoans, referralLoans + 1))}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Max: {currentLoans} (Self-generated loans earn premium {formatPercent(SELF_GEN_RATE)} rate)
                </p>
              </div>

              {/* Planned Additional Loans */}
              <div className="space-y-3 pt-4 border-t border-[#222]">
                <Label className="text-sm font-medium text-gray-300">Planned Additional Loans</Label>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPlannedLoans(Math.max(0, plannedLoans - 1))}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 text-center">
                    <div className="text-2xl font-bold text-white">{plannedLoans}</div>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPlannedLoans(plannedLoans + 1)}
                    className="h-10 w-10 rounded-lg border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                >
                  Calculate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setCurrentLoans(0);
                    setLoanAmount(0);
                    setBaseRate(2.75);
                    setReferralLoans(0);
                    setPlannedLoans(0);
                  }}
                  className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300"
                >
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Earnings Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Earnings */}
            <Card className="bg-[#111] border-[#222]">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-400">Current Earnings</span>
                    <DollarSign className="w-5 h-5 text-gray-500" />
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {formatCurrency(currentTotalEarnings)}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-[#1a1a1a] text-gray-300 border border-[#333]">
                      {formatPercent(currentTier.rate)} tier
                    </Badge>
                    {referralLoans > 0 && (
                      <Badge className="bg-green-900/50 text-green-400 border border-green-800">
                        Self-gen
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">From {currentLoans} closed loans</p>
                </div>
              </CardContent>
            </Card>

            {/* Projected Earnings */}
            <Card className="bg-gradient-to-br from-[#111] to-[#1a1a1a] border-[#333]">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-300">Projected Earnings</span>
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-green-400">
                    {formatCurrency(projectedTotalEarnings)}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-[#1a1a1a] text-gray-300 border border-[#333]">
                      {formatPercent(projectedTier.rate)} tier
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">From {totalPlannedLoans} total loans</p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Potential */}
            <Card className="bg-gradient-to-br from-green-950/30 to-[#111] border-green-900/50">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-300">Additional Potential</span>
                    <Award className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-green-400">
                    +{formatCurrency(additionalEarnings)}
                  </div>
                  <Badge className="bg-green-900/50 text-green-400 border border-green-800">
                    By closing {plannedLoans} more
                  </Badge>
                  <p className="text-xs text-green-500/70">Potential increase</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tier Progress */}
          <Card className="bg-[#111] border-[#222]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2 text-white">
                <Award className="w-5 h-5 text-gray-400" />
                <span>Commission Tier Progress</span>
              </CardTitle>
              <CardDescription className="text-gray-500">
                Your current tier and progress to the next level
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Tier Highlight */}
              <div className="bg-gradient-to-r from-[#1a1a1a] to-[#111] rounded-xl p-6 border-2 border-[#333]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm font-medium text-gray-400 mb-1">Current Tier</div>
                    <div className="text-3xl font-bold text-white">{currentTier.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-400 mb-1">Commission Rate</div>
                    <div className="text-3xl font-bold text-green-400">{formatPercent(currentTier.rate)}</div>
                  </div>
                </div>
                {nextTier && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400 font-medium">Progress to {nextTier.name}</span>
                      <span className="text-white font-semibold">{loansToNextTier} more loans</span>
                    </div>
                    <Progress value={tierProgress} className="h-3 bg-[#1a1a1a]" />
                  </div>
                )}
              </div>

              {/* All Tiers */}
              <div className={`grid gap-3 ${COMMISSION_TIERS.length <= 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-5'}`}>
                {COMMISSION_TIERS.map((tier, index) => {
                  const isActive = tier.name === currentTier.name;
                  const isPassed = currentLoans >= tier.loans;
                  return (
                    <div
                      key={tier.name}
                      className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                        isActive
                          ? 'border-green-600 bg-green-950/50'
                          : isPassed
                          ? 'border-[#333] bg-[#1a1a1a]'
                          : 'border-[#222] bg-[#111] opacity-60'
                      }`}
                    >
                      <div className="text-center space-y-2">
                        <div className={`text-sm font-semibold ${
                          isActive ? 'text-green-400' : isPassed ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {tier.name}
                        </div>
                        <div className={`text-2xl font-bold ${
                          isActive ? 'text-green-400' : isPassed ? 'text-white' : 'text-gray-600'
                        }`}>
                          {formatPercent(tier.rate)}
                        </div>
                        <div className={`text-xs ${
                          isActive ? 'text-green-500' : 'text-gray-500'
                        }`}>
                          {tier.loans}+ loan{tier.loans > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Self-Generated Info */}
              <div className="bg-gradient-to-r from-green-950/50 to-[#111] rounded-lg p-4 border border-green-900/50">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-green-400 mb-1">Self-Generated Bonus</div>
                    <p className="text-sm text-gray-300">
                      Loans you generate yourself earn a premium <strong className="text-green-400">{formatPercent(SELF_GEN_RATE)} commission rate</strong>, regardless of your current tier.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commission Breakdown */}
          <Card className="bg-[#111] border-[#222]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Commission Breakdown</CardTitle>
              <CardDescription className="text-gray-500">
                Detailed calculation of your current and projected earnings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Current Period */}
                <div>
                  <h4 className="font-semibold text-white mb-3">Current Period</h4>
                  <div className="space-y-2 text-sm">
                    {regularLoans > 0 && (
                      <div className="flex justify-between items-center py-2 border-b border-[#222]">
                        <span className="text-gray-400">
                          Regular loans ({regularLoans} × {formatCurrency(loanAmount)} × {formatPercent(currentTier.rate)})
                        </span>
                        <span className="font-semibold text-white">{formatCurrency(currentRegularEarnings)}</span>
                      </div>
                    )}
                    {referralLoans > 0 && (
                      <div className="flex justify-between items-center py-2 border-b border-[#222]">
                        <span className="text-gray-400">
                          Self-generated ({referralLoans} × {formatCurrency(loanAmount)} × {formatPercent(SELF_GEN_RATE)})
                        </span>
                        <span className="font-semibold text-green-400">{formatCurrency(currentReferralEarnings)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-3 bg-[#1a1a1a] px-3 rounded-lg border border-[#333]">
                      <span className="font-semibold text-white">Current Total</span>
                      <span className="font-bold text-xl text-white">{formatCurrency(currentTotalEarnings)}</span>
                    </div>
                  </div>
                </div>

                {/* Projected Total */}
                {plannedLoans > 0 && (
                  <div>
                    <h4 className="font-semibold text-white mb-3 flex items-center space-x-2">
                      <span>Projected Total</span>
                      <ArrowRight className="w-4 h-4 text-green-500" />
                    </h4>
                    <div className="space-y-2 text-sm">
                      {projectedRegularLoans > 0 && (
                        <div className="flex justify-between items-center py-2 border-b border-[#222]">
                          <span className="text-gray-400">
                            Regular loans ({projectedRegularLoans} × {formatCurrency(loanAmount)} × {formatPercent(projectedTier.rate)})
                          </span>
                          <span className="font-semibold text-white">{formatCurrency(projectedRegularEarnings)}</span>
                        </div>
                      )}
                      {referralLoans > 0 && (
                        <div className="flex justify-between items-center py-2 border-b border-[#222]">
                          <span className="text-gray-400">
                            Self-generated ({referralLoans} × {formatCurrency(loanAmount)} × {formatPercent(SELF_GEN_RATE)})
                          </span>
                          <span className="font-semibold text-green-400">{formatCurrency(projectedReferralEarnings)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-3 bg-gradient-to-r from-green-950/30 to-[#1a1a1a] px-3 rounded-lg border border-green-900/50">
                        <span className="font-semibold text-green-400">Projected Total</span>
                        <span className="font-bold text-xl text-green-400">{formatCurrency(projectedTotalEarnings)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CommissionCalculator;
